import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const KIND_TITLES = {
  "in-situ": "Candidature · Œuvres in situ",
  "dibond":  "Candidature Arts plastiques · peinture, photographie, illustration, sculpture, slam, écriture",
  "first-of-country": "Premier artiste de votre pays",
  "adhesion":  "Adhésion — Les Arteurs",
  "sponsoring": "Recevoir le dossier · Sponsoring",
  "mecenat":   "Recevoir le dossier · Mécénat",
  "don":       "Faire un don · Soutien",
  "artist":    "Candidature artiste",
  "adoption_artiste": "J'adopte un artiste · Dites-nous ce qui vous touche",
  "cercle_194": "Représenter mon pays · Le Cercle des 194",
  "benevolat_numerique": "Bénévole numérique · Rejoindre l'équipe",
};

// Meta-label au sommet du modal — différencié par nature de geste
const KIND_META_LABELS = {
  "adoption_artiste": "§ Adoption · Le Fil du Midi",
  "don":              "§ Don · Le Fil du Midi",
  "sponsoring":       "§ Sponsoring · Le Fil du Midi",
  "mecenat":          "§ Mécénat · Le Fil du Midi",
  "adhesion":         "§ Adhésion · Le Fil du Midi",
  "cercle_194":       "§ Cercle des 194 · Le Fil du Midi",
  "benevolat_numerique": "§ Bénévolat numérique · Le Fil du Midi",
};

// Libellé du bouton d'envoi — verbe d'engagement au lieu du générique
const KIND_SUBMIT_LABELS = {
  "adoption_artiste": (artistName) => artistName ? `J'adopte ${artistName}` : "J'adopte un artiste",
  "don":              () => "Je fais un don",
  "sponsoring":       () => "Je sponsorise",
  "mecenat":          () => "Je m'engage · Mécénat",
  "adhesion":         () => "J'adhère",
  "cercle_194":       () => "Je représente mon pays",
  "benevolat_numerique": () => "Je m'inscris",
};

// Redirection HelloAsso après validation (URL à confirmer avec Les Arteurs)
// TODO: remplacer par les URLs HelloAsso spécifiques une fois créées
const HELLOASSO_BASE = "https://www.helloasso.com/associations/les-arteurs";
const KIND_PAYMENT_URLS = {
  "don":        `${HELLOASSO_BASE}/formulaires/1/widget`,
  "sponsoring": `${HELLOASSO_BASE}/formulaires/2/widget`,
  "adhesion":   `${HELLOASSO_BASE}/adhesions/adhesion-2026`,
};

const KIND_INTROS = {
  "cercle_194": "Un mécène par pays. Un pays, une œuvre. Renseignez le pays que vous souhaitez représenter dans Le Fil du Midi — nous vous adresserons sous 5 jours ouvrés un dossier de représentation nominatif, la liste des pays encore disponibles et les modalités de contreparties (visibilité internationale, hospitalités, œuvre dédiée, mention gravée).",
  "adoption_artiste": "Adopter un artiste, c'est plus qu'un geste. C'est choisir de faire route avec quelqu'un — un univers, une main, un regard sur le monde. Dites-nous ce qui vous touche, et nous vous présenterons quelques artistes qui vous ressemblent. Aucune obligation : ce premier échange est fait pour vous éclairer.",
  "don":        "Un souffle qui compte. Renseignez vos coordonnées, nous vous ouvrons la porte du paiement sécurisé sur HelloAsso — vous choisissez le montant. Reçu fiscal automatique pour les particuliers (66 % de déduction) et les entreprises (60 %).",
  "sponsoring": "Associez votre marque à une aventure artistique de 750 km. Renseignez vos coordonnées, nous vous ouvrons la porte de la contribution sur HelloAsso, et nous revenons vers vous sous 5 jours ouvrés pour affiner contreparties et visibilité.",
  "mecenat":    "Engagement long, nom qui se grave. Renseignez vos coordonnées — nous vous adressons un dossier nominatif sous 5 jours ouvrés (loi Aillagon, jusqu'à 60 % de déduction fiscale) et un rendez-vous pour construire ensemble votre chapitre du Fil.",
  "adhesion":   "Franchissez la porte. Vernissages, ateliers, coulisses, rencontres : entrez dans la famille des Arteurs. Renseignez vos coordonnées, nous vous ouvrons la porte de l'adhésion sur HelloAsso — carte de membre envoyée sous 15 jours.",
  "benevolat_numerique": "Le Fil se tisse aussi en ligne. Community management, rédaction, veille presse, montage vidéo, référencement, traduction : dites-nous quelle main vous prêtez et sur quelle période. Nous revenons vers vous sous 5 jours pour caler ensemble.",
};

// Adoption profile — checkbox groups that feed the "project" text
const ADOPTION_UNIVERSES = ["Peinture figurative", "Peinture abstraite", "Sculpture monumentale", "Sculpture d'atelier", "Photographie documentaire", "Photographie plasticienne", "Installation in situ", "Land art", "Textile · fibre · tissage", "Céramique · argile", "Dessin · gravure · estampe", "Vidéo · film d'artiste", "Performance", "Art brut · outsider"];
const ADOPTION_GEOGRAPHIES = ["France · Occitanie", "Europe", "Afrique de l'Ouest", "Afrique du Nord", "Amérique du Sud", "Amérique du Nord", "Asie", "Océanie", "Moyen-Orient", "Toutes géographies confondues"];
const ADOPTION_MOMENTS = ["Un·e artiste émergent·e", "Un·e artiste mid-career", "Un·e artiste confirmé·e", "Une figure du territoire", "Un·e artiste international·e reconnu·e"];
const ADOPTION_ENGAGEMENTS = ["Adoption d'une œuvre existante", "Adoption d'une production nouvelle", "Résidence de création accompagnée", "Achat d'une pièce à l'issue du parcours", "Visibilité de mon nom sur l'œuvre", "Rencontre privilégiée avec l'artiste", "Visite d'atelier", "Mention gravée sur le parcours"];
const ADOPTION_BUDGETS = ["< 1 500 €", "1 500 – 5 000 €", "5 000 – 15 000 €", "15 000 – 50 000 €", "> 50 000 €", "À définir ensemble"];

const KIND_COUNTRY_LABELS = {
  "cercle_194": "Pays que vous représenteriez *",
};

const NEEDS_ARTIST_FIELDS = new Set(["in-situ", "dibond", "first-of-country", "artist"]);
const REDIRECT_ON_SUBMIT = new Set(["don", "sponsoring", "adhesion"]);
const PHONE_WHATSAPP_REQUIRED = new Set(["dibond", "in-situ", "first-of-country", "artist"]);

const KIND_TITLES_EN = {
  "in-situ": "Application · Works in situ",
  "dibond": "Visual arts application · painting, photography, illustration, sculpture, slam, writing",
  "first-of-country": "First artist from your country",
  "adhesion": "Membership — Les Arteurs",
  "sponsoring": "Receive the pack · Sponsorship",
  "mecenat": "Receive the pack · Patronage",
  "don": "Make a donation · Support",
  "artist": "Artist application",
  "adoption_artiste": "I adopt an artist · Tell us what moves you",
  "cercle_194": "Represent my country · The Circle of the 194",
  "benevolat_numerique": "Digital volunteer · Join the team",
};
const KIND_INTROS_EN = {
  "cercle_194": "One patron per country. One country, one artwork. Tell us which country you would like to represent in Le Fil du Midi — within 5 working days we will send you a personalised representation pack, the list of countries still available and the benefit terms (international visibility, hospitality, a dedicated artwork, engraved mention).",
  "adoption_artiste": "Adopting an artist is more than a gesture. It means choosing to travel with someone — a universe, a hand, a way of seeing the world. Tell us what moves you, and we will introduce you to a few artists who resemble you. No obligation: this first exchange is here to enlighten you.",
  "don": "A breath that counts. Fill in your details and we will open the door to secure payment — you choose the amount. Automatic tax receipt for individuals (66% deduction) and companies (60%).",
  "sponsoring": "Associate your brand with a 750 km artistic adventure. Fill in your details and we will get back to you within 5 working days to refine benefits and visibility.",
  "mecenat": "A long commitment, a name that is engraved. Fill in your details — we will send you a personalised pack within 5 working days (Aillagon Law, up to 60% tax deduction) and arrange a meeting to build your chapter of the Thread together.",
  "adhesion": "Walk through the door. Openings, workshops, backstage, encounters: join the Arteurs family. Fill in your details — membership card sent within 15 days.",
  "benevolat_numerique": "The Thread is also woven online. Community management, writing, press monitoring, video editing, SEO, translation: tell us which hand you can lend and for how long. We will get back to you within 5 days.",
};
const KIND_SUBMIT_LABELS_EN = {
  "adoption_artiste": (a) => a ? `I adopt ${a}` : "I adopt an artist",
  "don": () => "Make a donation",
  "sponsoring": () => "I sponsor",
  "mecenat": () => "I commit · Patronage",
  "adhesion": () => "I join",
  "cercle_194": () => "I represent my country",
  "benevolat_numerique": () => "I sign up",
};

export default function ApplicationModal({ open, onClose, kind = "artist", defaultCountry = "", artistName = "" }) {
  const { lang } = useLang();
  const en = lang === "en";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: defaultCountry,
    discipline: "", portfolio: "", project: "",
  });
  const [adopt, setAdopt] = useState({
    universes: [], geographies: [], moments: [], engagements: [], budget: "", specific: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const isAdoption = kind === "adoption_artiste";

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", phone: "", country: defaultCountry, discipline: "", portfolio: "", project: "" });
      setAdopt({ universes: [], geographies: [], moments: [], engagements: [], budget: "", specific: "" });
      setDone(false);
      // Force scroll to top of the modal overlay so the header + identity block are visible first
      setTimeout(() => {
        const overlay = document.querySelector('[data-testid="application-modal"]');
        if (overlay) overlay.scrollTop = 0;
      }, 50);
    }
  }, [open, defaultCountry]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const showArtistFields = NEEDS_ARTIST_FIELDS.has(kind);
  const isCercle = kind === "cercle_194";
  const requireCountry = showArtistFields || isCercle;
  const title = (en && KIND_TITLES_EN[kind]) || KIND_TITLES[kind] || (en ? "Application" : "Candidature");
  const intro = (en ? (KIND_INTROS_EN[kind] || "Fill in this form — we will reply quickly. A confirmation copy will be sent to you by email.") : (KIND_INTROS[kind] || "Remplissez ce formulaire — nous vous répondrons rapidement. Une copie confirmation vous sera envoyée par email."));
  const metaLabel = KIND_META_LABELS[kind] || "§ Candidature · Le Fil du Midi";
  const submitLabelFn = en ? KIND_SUBMIT_LABELS_EN[kind] : KIND_SUBMIT_LABELS[kind];
  const submitLabel = submitLabelFn ? submitLabelFn(artistName) : (en ? "Send my application" : "Envoyer ma candidature");
  const paymentUrl = KIND_PAYMENT_URLS[kind];
  const willRedirect = REDIRECT_ON_SUBMIT.has(kind) && paymentUrl;
  const phoneRequired = PHONE_WHATSAPP_REQUIRED.has(kind);
  const phoneLabel = phoneRequired ? (en ? "Phone · WhatsApp required *" : "Téléphone · WhatsApp obligatoire *") : (en ? "Phone" : "Téléphone");
  const countryLabel = (en && kind === "cercle_194") ? "Country you would represent *" : KIND_COUNTRY_LABELS[kind] || (en ? `Country of origin${showArtistFields ? " *" : ""}` : `Pays d'origine${showArtistFields ? " *" : ""}`);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // For adoption, compose a rich project description from the checkbox profile
      let projectText = form.project;
      if (isAdoption) {
        const parts = [];
        if (adopt.universes.length) parts.push(`Univers artistiques : ${adopt.universes.join(", ")}`);
        if (adopt.geographies.length) parts.push(`Géographies : ${adopt.geographies.join(", ")}`);
        if (adopt.moments.length) parts.push(`Profil d'artiste : ${adopt.moments.join(", ")}`);
        if (adopt.engagements.length) parts.push(`Formes d'engagement souhaitées : ${adopt.engagements.join(", ")}`);
        if (adopt.budget) parts.push(`Enveloppe envisagée : ${adopt.budget}`);
        if (adopt.specific.trim()) parts.push(`Un·e artiste précis·e en tête : ${adopt.specific.trim()}`);
        if (form.project.trim()) parts.push(`Message libre : ${form.project.trim()}`);
        projectText = parts.join("\n\n");
      }
      await api.post("/applications", { ...form, project: projectText, kind });
      // Redirection HelloAsso pour don/sponsoring/adhesion — sinon écran "Merci"
      if (willRedirect) {
        toast.success(en ? "Your details are saved. Redirecting…" : "Vos coordonnées sont enregistrées. Redirection…");
        setTimeout(() => { window.location.href = paymentUrl; }, 900);
      } else {
        setDone(true);
        toast.success(en ? "Application sent. You will receive a confirmation by email." : "Candidature envoyée. Vous recevrez une confirmation par email.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (key, value) => setAdopt((a) => ({
    ...a,
    [key]: a[key].includes(value) ? a[key].filter(v => v !== value) : [...a[key], value],
  }));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1000] bg-fg/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
          onClick={onClose}
          data-testid="application-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl bg-bg border border-fg/15 shadow-2xl my-8 md:my-12 mx-4"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center hover:bg-fg hover:text-bg transition-colors"
              aria-label="Fermer"
              data-testid="modal-close"
            >
              <X size={16} />
            </button>

            {done ? (
              <div className="p-10 md:p-16 text-center">
                <div className="w-20 h-20 rounded-full border-2 border-cinabre mx-auto flex items-center justify-center">
                  <CheckCircle2 size={32} className="text-cinabre" />
                </div>
                <h3 className="font-serif text-4xl md:text-5xl mt-8 leading-tight tracking-tight">{en ? "Thank you." : "Merci."}</h3>
                <p className="mt-4 font-serif italic text-xl md:text-2xl leading-relaxed text-fg/80 max-w-lg mx-auto">
                  {en ? "Your application has been recorded. A confirmation has been sent by email — we will get back to you quickly." : "Votre candidature est enregistrée. Une confirmation vous a été envoyée par email — nous reviendrons vers vous rapidement."}
                </p>
                <button onClick={onClose} className="btn-editorial ghost mt-10" data-testid="modal-close-done">{en ? "Close" : "Fermer"}</button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-8 md:p-10" data-testid="application-form">
                <div className="meta-label text-cinabre">{metaLabel}</div>
                <h3 className="font-serif text-3xl md:text-5xl mt-3 leading-[1.05] tracking-tight">{title}</h3>
                <p className="mt-4 text-fg/70 leading-relaxed max-w-lg">
                  {intro}
                </p>

                {/* Hint pour Adhésion : renvoi vers /artistes si candidature à exposer */}
                {kind === "adhesion" && (
                  <p className="mt-4 text-[13px] text-fg/60 leading-relaxed max-w-lg italic">
                    {en ? <>→ Would you like to <strong>apply to exhibit</strong> an artwork on the Thread? It's <a href="/artistes" className="underline decoration-cinabre decoration-1 underline-offset-4 hover:text-cinabre transition-colors">over here, on "Apply"</a>.</> : <>→ Vous souhaitez <strong>candidater pour exposer</strong> une œuvre sur Le Fil ? C'est <a href="/artistes" className="underline decoration-cinabre decoration-1 underline-offset-4 hover:text-cinabre transition-colors">par ici, sur « Je postule »</a>.</>}
                  </p>
                )}

                {/* § Vos coordonnées — panneau distinct, accent cinabre, impossible à manquer */}
                <div
                  className="mt-10 border-l-2 border-cinabre bg-cinabre/[0.04] p-6 md:p-8"
                  data-testid="identity-block"
                >
                  <div className="meta-label text-cinabre text-[11px]">§ {en ? "Your details · so we can reply to you personally" : "Vos coordonnées · pour vous répondre personnellement"}</div>
                  <p className="mt-3 font-serif italic text-lg md:text-xl text-fg/85 leading-snug max-w-xl">
                    {isAdoption
                      ? (en ? "An email, a meeting, a studio visit — everything starts with you." : "Un mail, un rendez-vous, une visite d'atelier — c'est par vous que tout commence.")
                      : (en ? "So we can reply to you personally — we never share your data." : "Pour vous répondre personnellement — nous ne partageons jamais vos données.")}
                  </p>

                  <div className="mt-6 grid md:grid-cols-2 gap-x-6 gap-y-5">
                    <label className="block">
                      <span className="meta-label text-fg/60">{en ? "Name / First name *" : "Nom / Prénom *"}</span>
                      <input required value={form.name} onChange={update("name")}
                        className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                        data-testid="form-name" />
                    </label>
                    <label className="block">
                      <span className="meta-label text-fg/60">Email *</span>
                      <input required type="email" value={form.email} onChange={update("email")}
                        className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                        data-testid="form-email" />
                    </label>
                    <label className="block">
                      <span className="meta-label text-fg/60">{phoneLabel}</span>
                      <input required={phoneRequired} value={form.phone} onChange={update("phone")}
                        placeholder={phoneRequired ? "+33 6 12 34 56 78" : ""}
                        className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                        data-testid="form-phone" />
                    </label>
                    <label className="block">
                      <span className="meta-label text-fg/60">{countryLabel}</span>
                      <input required={requireCountry} value={form.country} onChange={update("country")}
                        className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                        data-testid="form-country" />
                    </label>
                  </div>
                </div>

                <div className="mt-8 grid md:grid-cols-2 gap-6">
                  {showArtistFields && (
                    <>
                      <label className="block md:col-span-2">
                        <span className="meta-label text-fg/60">{en ? "Discipline (painting, sculpture, slam, writing, photography…)" : "Discipline (peinture, sculpture, slam, écriture, photographie…)"}</span>
                        <input value={form.discipline} onChange={update("discipline")}
                          className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                          data-testid="form-discipline" />
                      </label>
                      <label className="block md:col-span-2">
                        <span className="meta-label text-fg/60">{en ? "Portfolio / Website (URL)" : "Portfolio / Site web (URL)"}</span>
                        <input value={form.portfolio} onChange={update("portfolio")}
                          placeholder="https://…"
                          className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                          data-testid="form-portfolio" />
                      </label>
                    </>
                  )}
                  {isAdoption && (
                    <div className="md:col-span-2 space-y-8 mt-4">
                      <AdoptionGroup
                        label={en ? "§ Which universes speak to you most?" : "§ Quels univers vous parlent le plus ?"}
                        options={ADOPTION_UNIVERSES}
                        selected={adopt.universes}
                        onToggle={(v) => toggle("universes", v)}
                        testid="adopt-univers"
                      />
                      <AdoptionGroup
                        label={en ? "§ Which geographies call to you?" : "§ Quelles géographies vous appellent ?"}
                        options={ADOPTION_GEOGRAPHIES}
                        selected={adopt.geographies}
                        onToggle={(v) => toggle("geographies", v)}
                        testid="adopt-geo"
                      />
                      <AdoptionGroup
                        label={en ? "§ Which artist profile?" : "§ Quel profil d'artiste ?"}
                        options={ADOPTION_MOMENTS}
                        selected={adopt.moments}
                        onToggle={(v) => toggle("moments", v)}
                        testid="adopt-moment"
                      />
                      <AdoptionGroup
                        label={en ? "§ Which forms of commitment?" : "§ Quelles formes d'engagement ?"}
                        options={ADOPTION_ENGAGEMENTS}
                        selected={adopt.engagements}
                        onToggle={(v) => toggle("engagements", v)}
                        testid="adopt-eng"
                      />
                      <div>
                        <div className="meta-label text-fg/60 mb-3">§ {en ? "Envisaged budget (optional)" : "Enveloppe envisagée (optionnel)"}</div>
                        <div className="flex flex-wrap gap-2">
                          {ADOPTION_BUDGETS.map((b) => {
                            const active = adopt.budget === b;
                            return (
                              <button
                                type="button"
                                key={b}
                                onClick={() => setAdopt((a) => ({ ...a, budget: active ? "" : b }))}
                                className={`px-3.5 py-1.5 text-[13px] font-serif border transition-all ${active ? "bg-cinabre text-bg border-cinabre" : "bg-transparent text-fg/80 border-fg/25 hover:border-cinabre hover:text-cinabre"}`}
                                data-testid={`adopt-budget-${b.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                              >{b}</button>
                            );
                          })}
                        </div>
                      </div>
                      <label className="block">
                        <span className="meta-label text-fg/60">{en ? "A specific artist in mind? (optional)" : "Un·e artiste précis·e en tête ? (optionnel)"}</span>
                        <input
                          value={adopt.specific}
                          onChange={(e) => setAdopt((a) => ({ ...a, specific: e.target.value }))}
                          placeholder="Ex. Olga Yaméogo, Arno Brignon, Michel Batlle…"
                          className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre placeholder:text-fg/30"
                          data-testid="adopt-specific"
                        />
                      </label>
                    </div>
                  )}
                  <label className="block md:col-span-2">
                    <span className="meta-label text-fg/60">{isCercle ? (en ? "Your motivation · why this country?" : "Votre motivation · pourquoi ce pays ?") : (isAdoption ? (en ? "A free word to tell us more (optional)" : "Un mot libre pour nous en dire plus (optionnel)") : (showArtistFields ? (en ? "Project · approach · message" : "Projet · démarche · message") : "Message"))}</span>
                    <textarea rows={4} value={form.project} onChange={update("project")}
                      className="mt-2 w-full border border-fg/20 bg-transparent p-4 font-serif text-lg leading-relaxed focus:outline-none focus:border-cinabre resize-vertical"
                      data-testid="form-project" />
                  </label>
                </div>

                <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                    {en ? "By sending, you agree to be contacted by Les Arteurs." : "En envoyant, vous acceptez d'être recontacté(e) par Les Arteurs."}
                  </div>
                  <button type="submit" disabled={loading} className="btn-editorial" data-testid="form-submit">
                    {loading ? (en ? "Sending…" : "Envoi…") : submitLabel} {willRedirect ? <ExternalLink size={14} /> : <Send size={14} />}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AdoptionGroup({ label, options, selected, onToggle, testid }) {
  return (
    <div>
      <div className="meta-label text-fg/60 mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              type="button"
              key={opt}
              onClick={() => onToggle(opt)}
              className={`px-3.5 py-1.5 text-[13px] font-serif border transition-all ${active ? "bg-cinabre text-bg border-cinabre" : "bg-transparent text-fg/80 border-fg/25 hover:border-cinabre hover:text-cinabre"}`}
              data-testid={`${testid}-${opt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
            >{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

